import {
  ClientEvent,
  EventTimeline,
  KnownMembership,
  MatrixEvent,
  Room,
  RoomEvent,
  RoomStateEvent,
} from 'matrix-js-sdk/src/matrix';
import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getMessengerMeetingStartedAt } from 'modules/Messenger/components/MessengerCalls/MeetingSidebarView/meetingSidebarUtils';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents, isValidRoomScheduledStateEvent, waitForRoomSync } from 'utils/matrixUtils';
import { isMessengerMeetingSessionEnded } from 'utils/messengerMeetingLifecycleUtils';
import { getCurrentOrNextOccurrenceWindow, JOIN_PREROOM_THRESHOLD_MS } from 'utils/occurrenceWindow';
import { createAction, GetActionTypes } from 'utils/reducer/actions';
import { getMessengerRoomId } from 'utils/roomHierarchyUtils';

import { InitialRoomIdentityState, RoomIdentityContextType, RoomIdentityState } from './types';
import useRoomIdentityRetrieve from './useRoomIdentityRetrieve';
import useScheduledStartPolling from './useScheduledStartPolling';
import { defaultIdentityState, hasRoomIdInIdentityState, IdentityRetrieveStatus } from './utils';

export const actions = {
  setValues: (values: Partial<RoomIdentityState>) => createAction('setValues', values),
};

const roomIdentityReducer = (
  state: InitialRoomIdentityState,
  action: GetActionTypes<typeof actions>
): InitialRoomIdentityState => {
  switch (action.type) {
    case 'setValues':
      return { ...state, ...action.payload };
    default: {
      throw new Error(`Unsupported action type: ${action}`);
    }
  }
};

export const RoomIdentityContext = createContext<RoomIdentityContextType | null>(null);

const RoomIdentityProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(roomIdentityReducer, defaultIdentityState);
  const { matrixClient } = useMatrixContext();
  const params = useParams();

  const handleIdentityRetrieve = (values: Partial<RoomIdentityState>) => dispatch(actions.setValues(values));
  const { identityRetrieveStatus } = useRoomIdentityRetrieve(handleIdentityRetrieve);

  const { inviteToken } = params;

  // When polling reports a new scheduled start AND the meeting is not yet
  // live, the organiser has rescheduled — flag the update so
  // `ScheduledRoomJoinForm` can render the Screen 8 banner with the old
  // slot in strikethrough. Once `isStarted` flips to true (handled in the
  // matrix-timeline `useEffect` below), the flag is cleared so a stale
  // banner does not leak into the live-meeting screens.
  const onScheduledStartChange = useCallback(
    (scheduledStart: string, scheduledEnd?: string) => {
      // BUG-074: an edit that only extends the end time (e.g. 16:00 → 16:00–17:00)
      // must still sync into state. Comparing start alone made end-only updates a
      // no-op, so the clock/window kept the stale duration. Treat a changed end as
      // a schedule update too.
      const startChanged = scheduledStart !== state.startDate;
      const endChanged = !!scheduledEnd && scheduledEnd !== state.endsAt;
      if (!startChanged && !endChanged) return;
      const isScheduleUpdate = !!state.startDate && !state.isStarted;
      dispatch(
        actions.setValues({
          ...(startChanged ? { startDate: scheduledStart } : {}),
          ...(endChanged ? { endsAt: scheduledEnd } : {}),
          ...(isScheduleUpdate
            ? { isScheduleUpdated: true, previousStartDate: state.startDate }
            : { isScheduleUpdated: false, previousStartDate: undefined }),
        })
      );
    },
    [state.startDate, state.endsAt, state.isStarted]
  );

  // Cancellation detection flag (F-19): set when polling discovers the
  // meeting was cancelled while a user was on the pre-meeting page.
  // Surfaced via context so `ScheduledRoomJoinForm` can render the
  // cancelled state inline (Screens 6 / 7) — replaces the legacy
  // `Navigate to="/404"` redirect (T-008 / spec AC-11).
  const [isCancelled, setIsCancelled] = useState(false);
  // Cancellation is "cancel all" today (per-occurrence cancel ships in a
  // later PR). Clear `nextOccurrenceAt` so the cancelled card never shows a
  // stale "Next meeting" line — it was populated from the occurrence window
  // at identity-retrieval time, before the cancellation was known (BUG-010).
  const handleMeetingCancelled = useCallback(() => {
    setIsCancelled(true);
    dispatch(actions.setValues({ nextOccurrenceAt: undefined }));
  }, [dispatch]);

  // BUG-007 Fix A: called by the polling hook when a recurring meeting has already
  // started early. Flips isStarted so pre-joined participants are admitted within
  // the next poll cycle (~10 s) without waiting for wall-clock scheduled_at.
  const handleMeetingStarted = useCallback(() => {
    if (!state.isStarted) dispatch(actions.setValues({ isStarted: true }));
  }, [state.isStarted, dispatch]);

  useScheduledStartPolling({
    roomId: state.roomId,
    currentScheduledStart: state.startDate,
    currentScheduledEnd: state.endsAt,
    onScheduledStartChange,
    onMeetingStarted: handleMeetingStarted,
    matrixClient,
    inviteToken,
    onMeetingCancelled: handleMeetingCancelled,
  });

  // Reset the cancellation flag when the URL changes — otherwise a stale
  // "cancelled" state would persist across navigations.
  useEffect(() => {
    setIsCancelled(false);
  }, [params]);

  // BUG-021: make explicit-end detection reactive. `hasExplicitEndForCurrentOccurrence`
  // is a useMemo over the messenger timeline; without a "new MEETING_ENDED arrived"
  // signal it returns a stale value when the owner ends the meeting mid-session,
  // leaving a joining participant stuck on the "Joining meeting…" screen. This
  // counter increments whenever a MEETING_ENDED for THIS call room lands on the
  // linked messenger room's timeline, forcing the memo (and isEnded) to recompute.
  const [endEventVersion, setEndEventVersion] = useState(0);
  // #8185 / reload race: identity can resolve before the linked messenger room is in the
  // client cache. Wait for sync instead of fail-closed /404 on first paint.
  const [messengerGateSettled, setMessengerGateSettled] = useState(false);
  useEffect(() => {
    if (!state.roomId) return;

    // BUG-053: register GLOBAL listeners filtered by meeting_room_id rather than
    // resolving the messenger room first. Previously this effect early-returned when
    // `getMessengerRoomId(callRoom)` was null — which happens for scheduled-meeting
    // call rooms whose MESSENGER_CALL_STATE.messenger_room_id is not yet set when a
    // participant waits in the pre-room. With no listener registered, endEventVersion
    // never incremented and the participant sat on the join form for ~1 min until the
    // next background recompute. A global timeline listener filtered on the call room's
    // meeting_room_id catches the MEETING_ENDED regardless of which room it lands on.
    const handleTimelineEvent = (event: MatrixEvent) => {
      if (event.getType() !== CustomMeedioMatrixEvents.MEETING_ENDED) return;
      if ((event.getContent()?.meeting_room_id as string | undefined) !== state.roomId) return;
      setEndEventVersion((version) => version + 1);
    };

    // BUG-033: MESSENGER_CALL state clearance is a much faster end signal than the
    // MEETING_ENDED timeline event (state events sync ahead of timeline pagination).
    // When the owner ends the meeting, MESSENGER_CALL is cleared synchronously — bump
    // endEventVersion so hasExplicitEndForCurrentOccurrence / isEnded recompute without
    // waiting for MEETING_ENDED. Guard on the PREVIOUS call_room_id matching this room
    // (BUG-006 pattern) so unrelated / empty-to-empty clears don't false-trigger.
    // Listen globally on the client (state events route through RoomStateEvent.Events).
    const handleCallStateClear = (event: MatrixEvent) => {
      if (event.getType() !== CustomMeedioMatrixEvents.MESSENGER_CALL) return;
      if (event.getContent()?.call_room_id) return; // not a clear
      if ((event.getPrevContent()?.call_room_id as string | undefined) !== state.roomId) return;
      setEndEventVersion((version) => version + 1);
    };

    matrixClient.on(RoomEvent.Timeline, handleTimelineEvent);
    matrixClient.on(RoomStateEvent.Events, handleCallStateClear);
    return () => {
      matrixClient.off(RoomEvent.Timeline, handleTimelineEvent);
      matrixClient.off(RoomStateEvent.Events, handleCallStateClear);
    };
  }, [matrixClient, state.roomId]);

  useEffect(() => {
    if (!state.messengerRoomId) {
      setMessengerGateSettled(false);
      return;
    }

    if (matrixClient.getRoom(state.messengerRoomId)) {
      setMessengerGateSettled(true);
      return;
    }

    let cancelled = false;
    setMessengerGateSettled(false);

    waitForRoomSync(matrixClient, state.messengerRoomId, 8000).finally(() => {
      if (!cancelled) setMessengerGateSettled(true);
    });

    const onRoom = (room: Room) => {
      if (room.roomId === state.messengerRoomId) setMessengerGateSettled(true);
    };
    matrixClient.on(ClientEvent.Room, onRoom);

    return () => {
      cancelled = true;
      matrixClient.off(ClientEvent.Room, onRoom);
    };
  }, [matrixClient, state.messengerRoomId]);

  // BUG-002 (A.2): subscribe to `RoomStateEvent.Events` instead of
  // `RoomEvent.Timeline`. `RoomStateEvent.Events` is matrix-js-sdk's canonical
  // channel for state-event updates and fires from `RoomState.setStateEvents`
  // regardless of whether the SDK routed the event through the sync `state` or
  // `timeline` block (see sync.js:1629-1635, room-state.js:353). The previous
  // `Timeline` subscription missed state events delivered via the `state`
  // section (limited syncs, history-import paths) and the listener also
  // re-bound on every `state.startDate` change — dropping events that arrived
  // in the gap. `state.startDate` is intentionally NOT in the dep array now;
  // the dispatched updates flow through the reducer without needing a fresh
  // listener closure. The polling fallback (useScheduledStartPolling) covers
  // non-member participants who can't `getRoom(state.roomId)`.
  useEffect(() => {
    if (!state.roomId || state.isStarted) return;

    const room = matrixClient.getRoom(state.roomId);
    if (!room) return;

    const handleScheduledStateEvent = (event: MatrixEvent) => {
      if (event.getType() !== CustomMeedioMatrixEvents.IS_SCHEDULED_CALL_STATE_EVENT) return;

      const content = event.getContent();
      if (!isValidRoomScheduledStateEvent(content)) return;

      const startsAt =
        typeof content.startsAt === 'string'
          ? content.startsAt
          : content.startsAt instanceof Date
          ? content.startsAt.toISOString()
          : undefined;

      if (content.isStarted && startsAt) {
        // Clear any pending schedule-update banner flags on the
        // started-transition — once the meeting is live, the Screen 8
        // banner must not leak into Screen 4 / 5.
        dispatch(
          actions.setValues({
            startDate: startsAt,
            isStarted: true,
            isScheduleUpdated: false,
            previousStartDate: undefined,
          })
        );
      } else if (startsAt !== state.startDate) {
        dispatch(actions.setValues({ startDate: startsAt }));
      }
    };

    const roomState = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
    roomState?.on(RoomStateEvent.Events, handleScheduledStateEvent);
    return () => {
      roomState?.off(RoomStateEvent.Events, handleScheduledStateEvent);
    };
    // `state.startDate` is intentionally absent from deps — the handler reads
    // it via closure but we must NOT re-bind the listener on every countdown
    // tick or we drop events in the gap between unbind and re-bind.
    // The polling fallback (useScheduledStartPolling) covers missed updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.roomId, state.isStarted, matrixClient, dispatch]);

  // BUG-070: a pre-joined participant on the pre-room page has clicked Join but
  // has not joined the Matrix call room yet, so the call-room
  // IS_SCHEDULED_CALL_STATE_EVENT listener above never fires for them. When the
  // organiser starts the meeting (early or on time), the live signals land on the
  // linked MESSENGER room instead: a `MESSENGER_CALL` state event pointing at this
  // call room and a `MEETING_STARTED` timeline event for this `meeting_room_id`.
  // Mirror the BUG-053 GLOBAL-listener pattern (filter by this call room's id, no
  // local membership required) so `isStarted` flips and `useScheduledRoomStart`
  // admits the pre-joined participant instead of leaving them on the countdown.
  useEffect(() => {
    if (!state.roomId || state.isStarted) return;

    const handleMeetingStartedTimeline = (event: MatrixEvent) => {
      if (event.getType() !== CustomMeedioMatrixEvents.MEETING_STARTED) return;
      const content = event.getContent() ?? {};
      if ((content.meeting_room_id as string | undefined) !== state.roomId) return;
      const startedAt = typeof content.started_at === 'string' ? content.started_at : undefined;
      dispatch(
        actions.setValues({
          isStarted: true,
          ...(startedAt ? { startDate: startedAt } : {}),
          isScheduleUpdated: false,
          previousStartDate: undefined,
        })
      );
    };

    const handleMessengerCallState = (event: MatrixEvent) => {
      if (event.getType() !== CustomMeedioMatrixEvents.MESSENGER_CALL) return;
      if ((event.getContent()?.call_room_id as string | undefined) !== state.roomId) return;
      dispatch(actions.setValues({ isStarted: true }));
    };

    matrixClient.on(RoomEvent.Timeline, handleMeetingStartedTimeline);
    matrixClient.on(RoomStateEvent.Events, handleMessengerCallState);
    return () => {
      matrixClient.off(RoomEvent.Timeline, handleMeetingStartedTimeline);
      matrixClient.off(RoomStateEvent.Events, handleMessengerCallState);
    };
  }, [state.roomId, state.isStarted, matrixClient, dispatch]);

  const isLoading = [IdentityRetrieveStatus.INITIAL, IdentityRetrieveStatus.LOADING].includes(identityRetrieveStatus);

  useEffect(() => () => dispatch(actions.setValues(defaultIdentityState)), [params]);

  // Workspace-restricted meeting: block non-members before the join attempt.
  // The IS backend provides `is_team_meeting` + `workspace_matrix_room_id`
  // during identity seeding; when both are set and the user is not a member
  // of the workspace room, redirect to /404 instead of showing the join form.
  const isBlockedByWorkspace = useMemo(() => {
    if (!state.isTeamMeeting || !state.workspaceMatrixRoomId) return false;
    const wsRoom = matrixClient.getRoom(state.workspaceMatrixRoomId);
    return wsRoom?.getMyMembership() !== KnownMembership.Join;
  }, [state.isTeamMeeting, state.workspaceMatrixRoomId, matrixClient]);

  // Chat-linked meeting: block users who are not joined members of the linked
  // Messenger room. While the chat room is not yet in the client cache, stay on
  // LoadingScreen (messengerGateSettled) instead of redirecting to /404 — reload
  // often finishes identity before Matrix sync delivers the messenger room (#8185).
  const isMessengerGatePending = !!state.messengerRoomId && !messengerGateSettled;

  const isBlockedByMessenger = useMemo(() => {
    if (!state.messengerRoomId || !messengerGateSettled) return false;
    const chatRoom = matrixClient.getRoom(state.messengerRoomId);
    if (!chatRoom) return true;
    return chatRoom.getMyMembership() !== KnownMembership.Join;
  }, [state.messengerRoomId, matrixClient, messengerGateSettled]);

  // Explicit-end detection + next-recurrence advancement. These hooks must
  // run before the early returns below to satisfy React's rules of hooks.
  // Each guards on `state.roomId` / `state.endsAt` so they no-op cleanly
  // before identity retrieval completes.
  const meetingRoomForEndCheck = state.roomId ? matrixClient.getRoom(state.roomId) : null;
  // BUG-073: resolve the messenger room from the backend-provided id first, then
  // fall back to the call room's space-parent lookup. The backend id is available
  // before the user joins the call room, so end-detection (and the recurring
  // next-occurrence advancement) works on first paint instead of only after Join.
  const resolvedMessengerRoomId =
    state.messengerRoomId ?? (meetingRoomForEndCheck ? getMessengerRoomId(meetingRoomForEndCheck) : null);
  const hasExplicitEndForCurrentOccurrence = useMemo(() => {
    if (!state.roomId || !state.endsAt) return false;
    const messengerRoom = resolvedMessengerRoomId ? matrixClient.getRoom(resolvedMessengerRoomId) : null;
    if (!messengerRoom) return false;
    // BUG-003 Fix A: widen the occurrence start floor by JOIN_PREROOM_THRESHOLD_MS so
    // that a host end emitted before `scheduled_at` (in the pre-room join window) is
    // still detected. Mirrors `meetingSidebarUtils`, `announceMeetingLive`, and
    // `fireScheduledStart` which all apply this same widening.
    const occurrenceStartMs = state.startDate
      ? Date.parse(state.startDate) - JOIN_PREROOM_THRESHOLD_MS
      : Number.NEGATIVE_INFINITY;
    const occurrenceEndMs = Date.parse(state.endsAt);
    return messengerRoom
      .getLiveTimeline()
      .getEvents()
      .some((ev) => {
        if (ev.getType() !== CustomMeedioMatrixEvents.MEETING_ENDED) return false;
        const content = ev.getContent() ?? {};
        if (content.meeting_room_id !== state.roomId) return false;
        const ts = ev.getTs();
        if (ts < occurrenceStartMs || ts > occurrenceEndMs) return false;
        const endedBy = content.ended_by as string | undefined;
        const source = content.source as string | undefined;
        return !!endedBy && source !== 'delayed_event';
      });
    // `endEventVersion` is included so the memo recomputes when a MEETING_ENDED
    // for this room arrives on the messenger timeline mid-session (BUG-021). It is
    // not referenced in the body, so eslint flags it as unnecessary — but the
    // recompute is exactly the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixClient, resolvedMessengerRoomId, state.roomId, state.startDate, state.endsAt, endEventVersion]);

  // BUG-042: broader ended signal — any non-delayed MEETING_ENDED within the
  // current occurrence window. Includes hangup, banner-cleanup, and watchdog
  // paths that don't set `ended_by`. Used for join gates (pre-room screen,
  // auto-join guards) so that organically-ended meetings show the "Meeting has
  // ended" screen on rejoin. NOT used for recurring-series advancement (that
  // still requires the explicit host-end via `hasExplicitEndForCurrentOccurrence`).
  const hasAnyEndForCurrentOccurrence = useMemo(() => {
    if (!state.roomId || !state.endsAt) return false;
    const messengerRoom = resolvedMessengerRoomId ? matrixClient.getRoom(resolvedMessengerRoomId) : null;
    if (!messengerRoom) return false;
    // BUG-003 Fix A: widen the occurrence start floor by JOIN_PREROOM_THRESHOLD_MS
    // (mirror `hasExplicitEndForCurrentOccurrence` above).
    const occurrenceStartMs = state.startDate
      ? Date.parse(state.startDate) - JOIN_PREROOM_THRESHOLD_MS
      : Number.NEGATIVE_INFINITY;
    const occurrenceEndMs = Date.parse(state.endsAt);
    return messengerRoom
      .getLiveTimeline()
      .getEvents()
      .some((ev) => {
        if (ev.getType() !== CustomMeedioMatrixEvents.MEETING_ENDED) return false;
        const content = ev.getContent() ?? {};
        if (content.meeting_room_id !== state.roomId) return false;
        const ts = ev.getTs();
        if (ts < occurrenceStartMs || ts > occurrenceEndMs) return false;
        const source = content.source as string | undefined;
        return source !== 'delayed_event';
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixClient, resolvedMessengerRoomId, state.roomId, state.startDate, state.endsAt, endEventVersion]);

  // BUG-004: Extract the MESSENGER_CALL_STATE.ended check independently of
  // `isEnded` (which short-circuits immediately for recurring meetings via the
  // `state.isRecurring` guard). This value is `null` when the call room has not
  // ended, or the epoch-ms timestamp of `ended_at` when it has. Both `isEnded`
  // (for non-recurring, reusing rather than re-reading the state event) and the
  // recurring-occurrence advancement effect (below) use this value.
  //
  // `endEventVersion` is in deps so the memo recomputes when the MESSENGER_CALL
  // state event changes (e.g. the session ends while the pre-room is open).
  const callRoomEndedTs = useMemo((): number | null => {
    if (!meetingRoomForEndCheck) return null;
    const content = meetingRoomForEndCheck
      .getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL_STATE, '')
      ?.getContent();
    if (!content?.ended) return null;
    const endedAt = content.ended_at as string | undefined;
    return endedAt ? Date.parse(endedAt) : null;
    // `endEventVersion` is not referenced in the body but is intentionally in
    // deps to force a recompute when the MESSENGER_CALL state event changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingRoomForEndCheck, endEventVersion]);

  // Compute the next occurrence window strictly AFTER the current `endsAt` —
  // used to decide whether a recurring meeting should advance forward (and to
  // populate the new `startDate` / `endsAt` for the pre-meeting countdown).
  const nextRecurringWindow = useMemo(() => {
    if (!state.isRecurring || !state.recurrenceRule || !state.scheduledAt || !state.endsAt) return null;
    return getCurrentOrNextOccurrenceWindow(
      {
        scheduled_at: state.scheduledAt,
        ends_at: state.endsAt,
        is_recurring: true,
        recurrence_rule: state.recurrenceRule,
      },
      // `current_occurrence_*` is omitted so the helper falls back to RRULE
      // expansion, and `now` is set just past `endsAt` so the next start
      // strictly after the current window is returned.
      new Date(Date.parse(state.endsAt) + 1)
    );
  }, [state.isRecurring, state.recurrenceRule, state.scheduledAt, state.endsAt]);

  // When the current occurrence of a recurring meeting is definitively over,
  // advance the local state forward so the pre-meeting page shows a countdown
  // to the next occurrence instead of a blank / dead-end form.
  //
  // Two signals trigger advancement:
  //   1. `hasExplicitEndForCurrentOccurrence` — MEETING_ENDED with `ended_by`
  //      (explicit host end). Already window-bounded so it cannot be stale.
  //   2. `callRoomEndedTs` within the current occurrence window — covers organic
  //      ends (banner_cleanup, watchdog) where the MEETING_ENDED event has no
  //      `ended_by`. The call room's MESSENGER_CALL_STATE.ended persists across
  //      occurrences, so the occurrence-window check is critical to prevent
  //      repeated advancement on every render of future occurrences (BUG-004).
  useEffect(() => {
    if (!nextRecurringWindow) return;
    // BUG-029/BUG-056 guard — only skip if the current occurrence has NOT ended
    // AND this is a non-recurring meeting (recurring meetings always advance).
    if (state.endsAt && Date.now() < Date.parse(state.endsAt) && !state.isRecurring) return;

    let shouldAdvance = hasExplicitEndForCurrentOccurrence;
    if (!shouldAdvance && callRoomEndedTs !== null) {
      // BUG-004: MESSENGER_CALL_STATE.ended_at falls within the current occurrence
      // window → the session ended for this occurrence specifically.
      const occurrenceStartMs = state.startDate
        ? Date.parse(state.startDate) - JOIN_PREROOM_THRESHOLD_MS
        : Number.NEGATIVE_INFINITY;
      const occurrenceEndMs = state.endsAt ? Date.parse(state.endsAt) : Number.POSITIVE_INFINITY;
      shouldAdvance = callRoomEndedTs >= occurrenceStartMs && callRoomEndedTs <= occurrenceEndMs;
    }

    if (!shouldAdvance) return;

    const nextStartIso = new Date(nextRecurringWindow.startMs).toISOString();
    const nextEndIso = new Date(nextRecurringWindow.endMs).toISOString();
    if (nextStartIso === state.startDate && nextEndIso === state.endsAt) return;
    dispatch(
      actions.setValues({
        startDate: nextStartIso,
        endsAt: nextEndIso,
        nextOccurrenceAt: nextStartIso,
        isStarted: false,
      })
    );
  }, [
    hasExplicitEndForCurrentOccurrence,
    callRoomEndedTs,
    nextRecurringWindow,
    state.startDate,
    state.endsAt,
    state.isRecurring,
  ]);

  // Non-recurring "meeting has ended" gate. The original behaviour rendered
  // `<MeetingEndedPlaceholder />` directly — T-008 surfaces this through
  // context as `isEnded` so `ScheduledRoomJoinForm` can render the full
  // Screen 9 layout (with video preview + description) instead of the
  // legacy centred placeholder.
  //
  // Within-window rejoin: `ended_at` may be set by the user-triggered
  // `/end` endpoint the moment the last participant leaves, even when the
  // scheduled `ends_at` has not yet passed. The backend cron
  // (`MeetingEndCronService`) is the authoritative end-of-window mechanism
  // (sets `ended_at = ends_at` at `ends_at + 15 min` grace), so any
  // `ended_at` observed while the current wall-clock time is still before
  // `endsAt` must be treated as a premature close and the room must
  // remain rejoinable. The gate checks `now < endsAt` — a dynamic
  // wall-clock comparison — rather than a static `endedAt < endsAt`: the
  // static check would stay `true` forever after an early end, incorrectly
  // unlocking the room hours later. This also subsumes the older AV-check
  // reset (OD-ED-1 Option B): an AV-check start + end before
  // `scheduled_at` still falls inside the scheduled window, so the same
  // branch handles it. On rejoin `/start` returns `started_now: false`
  // (occurrence already started), so no duplicate notifications.
  //
  // Instant rooms have `endsAt = undefined` (no scheduled window). The
  // ended-at gate only applies to explicitly scheduled meetings.
  //
  // Recurring meetings never raise `isEnded` — they advance to the next
  // occurrence via the `nextRecurringWindow` effect above. See
  // `spec/ux.md` §Screen 9 — non-recurring only.
  const isEnded = useMemo(() => {
    if (!state.roomId || state.isRecurring) return false;

    const room = matrixClient.getRoom(state.roomId);
    const rtcSession = room ? matrixClient.matrixRTC.getActiveRoomSession(room) : null;
    const hasActiveRtcSession = !!rtcSession && rtcSession.memberships.length > 0;

    // BUG-003 Fix B: `callRoomEndedTs` (MESSENGER_CALL_STATE.ended) is the
    // authoritative "session over" signal. Computed in the standalone memo above
    // so it is also available to the recurring-occurrence advancement effect
    // (BUG-004) — `isEnded` short-circuits early for recurring meetings and
    // therefore cannot be the sole consumer of this signal.
    if (callRoomEndedTs !== null) return !hasActiveRtcSession;

    // BUG-042: any non-delayed end (hangup, banner-cleanup, watchdog — not just
    // host explicit-end) within the current occurrence window permanently closes
    // re-entry. `hasExplicitEndForCurrentOccurrence` is the stricter subset;
    // `hasAnyEndForCurrentOccurrence` handles all organic end paths.
    if (hasAnyEndForCurrentOccurrence) return !hasActiveRtcSession;
    // BUG-021: an explicit host-end (ended_by set, not a delayed last-person
    // hangup) is a permanent end — everyone is kicked — so isEnded must flip
    // true immediately, even while still inside the scheduled window. The
    // within-window guard below only applies to the backend `ended_at` path
    // (last-person hangup), which must stay rejoinable until `ends_at`.
    if (hasExplicitEndForCurrentOccurrence) return !hasActiveRtcSession;

    if (!state.endedAt || !state.endsAt) return false;
    const isWithinScheduledWindow = Date.now() < new Date(state.endsAt).getTime();
    if (isWithinScheduledWindow) return false;
    return !hasActiveRtcSession;
  }, [
    matrixClient,
    callRoomEndedTs,
    state.roomId,
    state.isRecurring,
    state.endedAt,
    state.endsAt,
    hasExplicitEndForCurrentOccurrence,
    hasAnyEndForCurrentOccurrence,
  ]);

  const isInstantMessengerCall = !!resolvedMessengerRoomId && !state.scheduledAt && !state.endsAt;

  const instantSessionStartedAt = useMemo(() => {
    if (!resolvedMessengerRoomId || !state.roomId) return undefined;
    return getMessengerMeetingStartedAt(matrixClient, resolvedMessengerRoomId, state.roomId) ?? undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixClient, resolvedMessengerRoomId, state.roomId, endEventVersion]);

  const instantSessionEndedAt = useMemo(() => {
    if (!meetingRoomForEndCheck || callRoomEndedTs === null) return undefined;
    const content = meetingRoomForEndCheck
      .getLiveTimeline()
      .getState(EventTimeline.FORWARDS)
      ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL_STATE, '')
      ?.getContent();
    return (content?.ended_at as string | undefined) ?? undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingRoomForEndCheck, callRoomEndedTs, endEventVersion]);

  const isInstantSessionEnded = useMemo(() => {
    if (!isInstantMessengerCall || !state.roomId) return false;

    const room = matrixClient.getRoom(state.roomId);
    const rtcSession = room ? matrixClient.matrixRTC.getActiveRoomSession(room) : null;
    const hasActiveRtcSession = !!rtcSession && rtcSession.memberships.length > 0;

    const endedByStamp = callRoomEndedTs !== null && !hasActiveRtcSession;

    const sessionStartedAtMs = instantSessionStartedAt ? Date.parse(instantSessionStartedAt) : null;
    const endedByTimeline =
      sessionStartedAtMs !== null &&
      !!resolvedMessengerRoomId &&
      isMessengerMeetingSessionEnded({
        matrixClient,
        messengerRoomId: resolvedMessengerRoomId,
        meetingRoomId: state.roomId,
        sessionStartedAtMs,
      });

    return endedByStamp || endedByTimeline || (isEnded && !state.endsAt);
    // `endEventVersion` is not referenced in the body but forces recompute when end
    // signals arrive mid-session (BUG-021 / #8185).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInstantMessengerCall,
    state.roomId,
    state.endsAt,
    matrixClient,
    callRoomEndedTs,
    instantSessionStartedAt,
    resolvedMessengerRoomId,
    isEnded,
    endEventVersion,
  ]);

  if (isLoading || isMessengerGatePending) return <LoadingScreen />;
  if (identityRetrieveStatus === IdentityRetrieveStatus.ERROR || !hasRoomIdInIdentityState(state)) {
    logger.error('RoomIdentityProvider: roomId is missing in the state, room was probably not found', { state });
    return <Navigate to="/404" />;
  }
  if (isBlockedByWorkspace || isBlockedByMessenger) {
    logger.info('RoomIdentityProvider: user is blocked by workspace or messenger', {
      isBlockedByWorkspace,
      isBlockedByMessenger,
    });
    return <Navigate to="/404" />;
  }

  // Explicit host-end within the current occurrence window for a recurring
  // series whose next occurrence has not yet been resolved: previously this
  // rendered `<MeetingEndedPlaceholder />`. For recurring meetings we keep
  // the same behaviour by routing through the existing /404 redirect
  // (recurring series with no future occurrence is rare — the
  // `nextRecurringWindow` effect handles the typical case before this gate
  // is reached). Non-recurring ended meetings now flow through `isEnded`
  // in the provider value below instead of an early return.
  if (hasExplicitEndForCurrentOccurrence && meetingRoomForEndCheck) {
    const rtcSession = matrixClient.matrixRTC.getActiveRoomSession(meetingRoomForEndCheck);
    const hasActiveRtcSession = !!rtcSession && rtcSession.memberships.length > 0;
    const hasFutureRecurrence = state.isRecurring && !!nextRecurringWindow;
    if (!hasActiveRtcSession && !hasFutureRecurrence && state.isRecurring) {
      return <Navigate to="/404" />;
    }
  }

  return (
    <RoomIdentityContext.Provider
      value={{
        state,
        actions,
        dispatch,
        isCancelled,
        isEnded,
        hasExplicitEndForCurrentOccurrence,
        hasAnyEndForCurrentOccurrence,
        isInstantSessionEnded,
        instantSessionStartedAt,
        instantSessionEndedAt,
      }}
    >
      {children}
    </RoomIdentityContext.Provider>
  );
};

export default RoomIdentityProvider;
